import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, map, Observable, of, startWith, switchMap } from 'rxjs';

import { EventTreeEngine, IData, IDisplayInfo } from './event-tree.class';
import { EventTreeState } from './event-tree.state';
import { isRecommendedEventTreeState } from './event-tree.utils';

import { shareReplayRefCount } from 'src/app/utils/operator.utils';

@Injectable({
    providedIn: 'root'
})
export class EventTreeService {
    private readonly _http = inject(HttpClient);
    private readonly _store = inject(Store);

    private readonly _eventTreeInstance$: Observable<EventTreeEngine | undefined> =
        this._store.select(EventTreeState.selectors.eventTreeInstance);

    /**
     * Bridges the NGXS-held engine to its own `displayInfo$` stream. Needed because
     * display updates emit from the engine without patching store state.
     */
    public displayInfo$: Observable<IDisplayInfo | undefined> = this._eventTreeInstance$.pipe(
        switchMap((engine) => {
            if (!engine) {
                return of(undefined);
            }

            return engine.displayInfo$.pipe(
                startWith(engine.displayInfo),
            );
        }),
        shareReplayRefCount()
    );

    public isRecommended$: Observable<boolean> = this.displayInfo$.pipe(
        map((eventTreeDisplayInfo) => isRecommendedEventTreeState(eventTreeDisplayInfo)),
        distinctUntilChanged()
    );

    public getEventTreeConfiguration(game_id: number, application_id: number): Observable<IData> {
        return this._http.get<IData>(`games/${game_id}/application/${application_id}/config`);
    }

    public postEventTreeConfiguration(game_id: number, application_id: number): Observable<IData> {
        return this._http.post<IData>(`games/${game_id}/application/${application_id}/config`, {});
    }
}
